import { Breadcrumbs, IconButton, Link, Menu, MenuItem, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { WsColors } from 'wisestep-base';

export interface IBreadCrumbsList {
  label: string;
  url: string;
  Icon?: React.ReactNode;
}
interface WsBreadCrumbsList extends IBreadCrumbsList {
  condensed?: boolean;
}

export interface WsBreadCrumbsProps {
  breadCrumbsList: IBreadCrumbsList[];
  separator?: React.ReactNode | string;
  maxItems?: number;
  breadCrumbSize?: number | string;
}

const linkStyles = (breadCrumbSize: string | number) => ({
  color: WsColors.TertiaryShades900,
  display: 'flex',
  alignItems: 'center',
  fontSize: breadCrumbSize,
  fontWeight: '400',
  px: 1,
  borderRadius: 1,
  cursor: 'pointer',
  '&:hover': {
    background: WsColors.TertiaryShades200,
  },
});

const isLastItem = (index: number, breadCrumbsListLength: number) =>
  index === breadCrumbsListLength - 1;

export const WsBreadCrumbs: React.FC<WsBreadCrumbsProps> = ({
  breadCrumbsList,
  separator = '>',
  maxItems = 3,
  breadCrumbSize = 14,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const condensedBreadcrumbs: WsBreadCrumbsList[] =
    breadCrumbsList.length > maxItems
      ? [
          ...breadCrumbsList.slice(0, maxItems - 2),
          { label: '...', url: '#', condensed: true },
          breadCrumbsList[breadCrumbsList.length - 1],
        ]
      : breadCrumbsList;

  const menuItems = breadCrumbsList.slice(maxItems - 2, breadCrumbsList.length - 1);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        aria-labelledby="condensed-breadcrumbs-menu"
      >
        {menuItems.map((breadcrumb, index) => (
          <MenuItem key={index} onClick={handleClose} sx={{ fontSize: breadCrumbSize }}>
            <Link component={RouterLink} to={breadcrumb.url} color="inherit" underline="none">
              {breadcrumb.label}
            </Link>
          </MenuItem>
        ))}
      </Menu>

      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<span style={{ fontSize: breadCrumbSize }}>{separator}</span>}
        sx={{ mb: 1 }}
      >
        {condensedBreadcrumbs?.map((crumb, index) =>
          crumb?.condensed ? (
            <IconButton
              key={`crumb-${index}`}
              sx={{
                color: WsColors.TertiaryShades700,
                fontSize: breadCrumbSize,
                borderRadius: 1,
                px: 1,
                '&:hover': {
                  background: WsColors.TertiaryShades300,
                },
              }}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              {crumb.label}
            </IconButton>
          ) : !isLastItem(index, condensedBreadcrumbs.length) ? (
            <Link
              key={`crumb-${index}`}
              component={RouterLink}
              to={crumb.url}
              underline="none"
              sx={linkStyles(breadCrumbSize)}
            >
              {crumb?.Icon}
              {crumb.label}
            </Link>
          ) : (
            <Typography fontWeight={600} fontSize={breadCrumbSize} color="black">
              {crumb?.Icon}
              {crumb.label}
            </Typography>
          )
        )}
      </Breadcrumbs>
    </>
  );
};
